import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PagesRoutes } from "@app/router";
import { useAuthStore } from "@app/store";
import { useRegisterUserApi } from "@entities/user";
import { Button } from "@shared/components";
import { useTranslation } from "@shared/locales";
import {
  validateConfirm,
  validateEmail,
  validateFirstName,
  validateLastName,
  validatePassword,
} from "../utils";
import type { FieldErrors } from "../utils";
import { AuthShell } from "./AuthShell";
import { FormField } from "./FormField";

type RegisterField =
  "firstName" | "lastName" | "email" | "password" | "confirm" | "terms";

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuthStore((state) => state.setSession);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState<FieldErrors<RegisterField>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const register = useRegisterUserApi({
    withoutToastMessage: true,
    navigateOnError: false,
    onSuccess: (session) => {
      setSession(session);
      navigate(PagesRoutes.home.index);
    },
    onError: (error) =>
      setServerError(error?.message ?? t("auth.errors.registerFailed")),
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setServerError(null);

    const nextErrors: FieldErrors<RegisterField> = {
      firstName: validateFirstName(firstName),
      lastName: validateLastName(lastName),
      email: validateEmail(email),
      password: validatePassword(password),
      confirm: validateConfirm(password, confirm),
      terms: acceptedTerms ? undefined : "auth.errors.termsRequired",
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    register.mutate({
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      password_confirmation: confirm,
    });
  };

  return (
    <AuthShell
      title={t("auth.register.title")}
      description={t("auth.register.description")}
      serverError={serverError}
      footer={
        <>
          {t("auth.register.haveAccount")}{" "}
          <Link
            to={PagesRoutes.auth.login}
            className="font-medium text-foreground hover:underline"
          >
            {t("auth.register.signIn")}
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <FormField
          id="register-first-name"
          label={t("auth.fields.firstName")}
          placeholder={t("auth.fields.firstNamePlaceholder")}
          autoComplete="given-name"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          error={errors.firstName && t(errors.firstName)}
        />
        <FormField
          id="register-last-name"
          label={t("auth.fields.lastName")}
          placeholder={t("auth.fields.lastNamePlaceholder")}
          autoComplete="family-name"
          value={lastName}
          onChange={(event) => setLastName(event.target.value)}
          error={errors.lastName && t(errors.lastName)}
        />
        <FormField
          id="register-email"
          type="email"
          label={t("auth.fields.email")}
          placeholder={t("auth.fields.emailPlaceholder")}
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={errors.email && t(errors.email)}
        />
        <FormField
          id="register-password"
          type="password"
          label={t("auth.fields.password")}
          placeholder="••••••••"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          hint={t("auth.fields.passwordHint")}
          error={errors.password && t(errors.password)}
        />
        <FormField
          id="register-confirm"
          type="password"
          label={t("auth.fields.confirmPassword")}
          placeholder="••••••••"
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          error={errors.confirm && t(errors.confirm)}
        />

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="register-terms"
            className="flex cursor-pointer items-start gap-2.5 text-sm text-muted-foreground"
          >
            <input
              id="register-terms"
              type="checkbox"
              checked={acceptedTerms}
              onChange={(event) => setAcceptedTerms(event.target.checked)}
              aria-invalid={!!errors.terms}
              aria-describedby={
                errors.terms ? "register-terms-error" : undefined
              }
              className="mt-0.5 h-4 w-4 rounded border-input accent-[hsl(var(--primary))]"
            />
            <span>{t("auth.register.terms")}</span>
          </label>
          {errors.terms ? (
            <p
              id="register-terms-error"
              className="text-[13px] text-destructive"
            >
              {t(errors.terms)}
            </p>
          ) : null}
        </div>

        <Button type="submit" className="w-full" disabled={register.isPending}>
          {register.isPending
            ? t("auth.register.submitting")
            : t("auth.register.submit")}
        </Button>
      </form>
    </AuthShell>
  );
}
